import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import type { EventCategory } from '#~/analytics/constants/event-categories.js';
import type { EventName } from '#~/analytics/constants/event-names.js';
import type { EventProperties } from '#~/analytics/types/properties.js';

type TrackFn = (name: EventName, category: EventCategory, properties: EventProperties) => void;

/** The parts of the camera's direct-link reply the Add Source dialog reads. */
export type AddSourceCredentialReply = {
  bridgeId?: string | undefined;
  lockoutStatus?: { isLocked?: boolean | undefined } | undefined;
  error?: { message?: string | undefined } | null | undefined;
};

/** What the dialog does next for one credential reply. */
export type AddSourceCredentialOutcome = 'locked' | 'rejected' | 'authenticated';

/**
 * Classify the camera's reply to an Add Source password and report it.
 *
 * WHY a separate module: the dialog's reply handling is the Add Source
 * credential boundary, and the e2e receipt drives this same function, so
 * removing an emit here reds the receipt as well as the dialog's unit suite.
 *
 * - A locked reply sends nothing. The saved auth-failure reports count
 *   `device_auth_invalid` with no filter, so a lockout row would read as more
 *   wrong passwords.
 * - A rejected password sends `device_auth_invalid` without `agent_id`. The
 *   camera firmware withholds its bridge id until the password is accepted
 *   (bridge/encoder/pkg/mngr/web/api.go), and it mints a new agent id for every
 *   link, so no agent id exists for this attempt.
 * - An accepted password sends `device_authenticate` with the agent the dialog
 *   is about to link (`bridgeId` of the link reply). That is the agent that
 *   later shadows, so the claim-to-stream join starts here.
 *
 * Per rule: reference-rules-in-comments (ai.knowledge/global).
 */
export const reportAddSourceCredentialReply = (input: {
  reply: AddSourceCredentialReply;
  authAnalyticsProps: EventProperties;
  track: TrackFn;
}): AddSourceCredentialOutcome => {
  const { reply, authAnalyticsProps, track } = input;

  if (reply.lockoutStatus?.isLocked === true) {
    return 'locked';
  }

  if (reply.error !== undefined && reply.error !== null) {
    track(EVENT_NAMES.DEVICE_AUTH_INVALID, EVENT_CATEGORIES.DEVICE, {
      errorMessage: reply.error.message,
      deviceType: 'onvif',
      ...authAnalyticsProps
    });
    return 'rejected';
  }

  track(EVENT_NAMES.DEVICE_AUTHENTICATE, EVENT_CATEGORIES.DEVICE, {
    deviceType: 'iris-enabled',
    ...authAnalyticsProps,
    ...(reply.bridgeId !== undefined && reply.bridgeId !== '' ? { agent_id: reply.bridgeId } : {})
  });
  return 'authenticated';
};
